import Vue from 'vue';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import ProductType from '@/models/product-type';
import Product, { ProductAvailability, ProductInterface } from '@/models/product';
import Step, { StepType } from '@/models/step';
import Ingredient from '@/models/ingredient';
import ProductOption, { OptionType } from '@/models/product-option';
@Module({ name: 'product_customizer', namespaced: true })
export default class ProductCustomizerModule extends VuexModule {
    productType: ProductType | null = null;
    currentProduct: Product | null = null;
    productInstructions: string = '';
    quantity: number = 1;
    stepsIngredients: [Step, Ingredient][] = [];
    stepsProductOptions: [Step, ProductOption][] = [];
    isDirty: boolean = false;

    @Mutation
    public setProductType(productType: ProductType | null) {
        this.productType = productType;
    }

    @Mutation
    public setCurrentProduct(product: Product | null) {
        this.currentProduct = product;
    }

    @Mutation
    public setDirty(dirty: boolean) {
        this.isDirty = dirty;
    }

    @Mutation
    public setProductInstructions(value: string) {
        const creditCardRegex = /(?:^|[^0-9])(?:\d[ -]*?){13,16}(?:$|[^0-9])/g;
        if (creditCardRegex.test(value)) {
            value.replace(creditCardRegex, '');
        }
        this.productInstructions = value;
        if (value.length > 0) {
            this.isDirty = true;
        }
    }

    @Mutation
    public setQuantity(quantity: number) {
        this.quantity = quantity;
    }

    @Mutation
    public removeAllIngredients() {
        this.stepsIngredients = [];
    }

    @Mutation
    public addIngredient(stepIngredient: [Step, Ingredient]) {
        let index = this.stepsIngredients.findIndex((stpIng: [Step, Ingredient]) => stpIng[0].id === stepIngredient[0].id && stpIng[1].id === stepIngredient[1].id);
        if (index === -1) {
            this.stepsIngredients.push(stepIngredient);
        }
    }

    @Mutation
    public updateIngredient(stepIngredient: [Step, Ingredient]) {
        let index = this.stepsIngredients.findIndex((stpIng: [Step, Ingredient]) => stpIng[0].id === stepIngredient[0].id && stpIng[1].id === stepIngredient[1].id);
        if (index !== -1) {
            Vue.set(this.stepsIngredients, index, stepIngredient);
        }
    }

    @Mutation
    public removeIngredient(stepIngredient: [Step, Ingredient]) {
        let index = this.stepsIngredients.findIndex((stpIng: [Step, Ingredient]) => stpIng[0].id === stepIngredient[0].id && stpIng[1].id === stepIngredient[1].id);
        if (index !== -1) {
            this.stepsIngredients.splice(index, 1);
        }
    }

    @Mutation
    public clearProductOptions() {
        this.stepsProductOptions = [];
    }

    @Mutation
    public toggleProductOption(stepProductOption: [Step, ProductOption]) {
        let index = this.stepsProductOptions.findIndex((stpPrdOpt: [Step, ProductOption]) => stpPrdOpt[0].id === stepProductOption[0].id && stpPrdOpt[1].id === stepProductOption[1].id);
        this.stepsProductOptions.splice(index, 1, stepProductOption);
    }

    @Mutation
    public addProductOption(stepProductOption: [Step, ProductOption]) {
        let index = this.stepsProductOptions.findIndex((stpPrdOpt: [Step, ProductOption]) => stpPrdOpt['0'].id === stepProductOption[0].id && stpPrdOpt[1].id === stepProductOption[1].id);
        if (index === -1) {
            this.stepsProductOptions.push(stepProductOption);
        }
    }

    @Mutation
    public removeProductOption(stepProductOption: [Step, ProductOption]) {
        let index = this.stepsProductOptions.findIndex((stpPrdOpt: [Step, ProductOption]) => stpPrdOpt[0].id === stepProductOption[0].id && stpPrdOpt[1].id === stepProductOption[1].id);
        if (index !== -1) {
            this.stepsProductOptions.splice(index, 1);
        }
    }

    @Action
    public reset() {
        this.setDirty(false);
        this.setProductInstructions('');
        this.setQuantity(1);
        this.clearIngredients();
        this.clearProductOptions();
        this.setCurrentProduct(null);
        this.setProductType(null);
    }

    @Action
    public clearIngredients(step?: Step) {
        if (step !== undefined) {
            const stepIngredients = this.stepsIngredients.filter((stepIngredient: [Step, Ingredient]) => stepIngredient[0].id === step.id);
            for (let index = 0; index < stepIngredients.length; index++) {
                this.removeIngredient(stepIngredients[index]);
            }
        } else {
            this.removeAllIngredients();
        }
    }

    @Action({ commit: 'setProductType' })
    public async fetchProductType(slug: string) {
        return await ProductType.findBySlug(slug);
    }

    @Action
    public async setStepsIngredients(steps: Step[]) {
        if (this.currentProduct && this.productType) {
            this.clearIngredients();
            for (let index = 0; index < this.currentProduct.ingredients.length; index++) {
                const ingredient = this.currentProduct.ingredients[index];
                for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
                    if (steps[stepIndex].ingredients.map((ing) => ing.id).indexOf(ingredient.id) !== -1) {
                        this.addIngredient([steps[stepIndex], ingredient]);
                    }
                }
            }
        }
    }

    @Action
    public async setStepsProductOptions(steps: Step[]) {
        if (this.productType) {
            this.clearProductOptions();
            if (this.currentProduct) {
                for (let index = 0; index < this.currentProduct.options.length; index++) {
                    const option = this.currentProduct.options[index];
                    if (option.value !== undefined) {
                        const step = steps.find((step: Step) => step.product_options.map((opt) => opt.id).indexOf(option.id) !== -1);
                        if (step) {
                            this.addProductOption([step, option]);
                        }
                    }
                }
            }
            for (let index = 0; index < steps.length; index++) {
                if (steps[index].type === StepType.OPTIONS) {
                    const options = steps[index].product_options;
                    for (let i = 0; i < options.length; i++) {
                        options[i].value = options[i].default_value;
                        this.addProductOption([steps[index], options[i]]);
                    }
                }
            }
        }
    }

    get stepIngredientsCount() {
        return (step: Step) => {
            let ingredients = this.stepIngredients(step);
            let total = 0;
            for (let i = 0; i < ingredients.length; i++) {
                const ingredient = ingredients[i];
                if (ingredient.quantity !== undefined) {
                    total += ingredient.quantity;
                }
            }
            return total;
        };
    }

    get stepProductOptionsCount() {
        return (step: Step) => {
            let productOptions = this.stepProductOptions(step);
            let total = 0;
            for (let i = 0; i < productOptions.length; i++) {
                const productOption = productOptions[i];
                if ((productOption.type === OptionType.CHECKBOX && productOption.value == 'true') || productOption.value == 'yes' || (productOption.type === OptionType.RADIO && productOption.value)) {
                    total++;
                }
            }
            return total;
        };
    }

    get stepIngredients() {
        return (step: Step) => this.stepsIngredients.filter((stepIngredient: [Step, Ingredient]) => stepIngredient[0].id === step.id).map((stepIngredient) => stepIngredient[1]);
    }

    get isIngredientSelected() {
        return (stepIngredient: [Step, Ingredient]) => {
            let index = this.stepsIngredients.findIndex((stpIng: [Step, Ingredient]) => stpIng[0].id === stepIngredient[0].id && stpIng[1].id === stepIngredient[1].id);
            return index !== -1;
        };
    }

    get ingredientQuantity() {
        return (stepIngredient: [Step, Ingredient]) => {
            let stpIng = this.stepsIngredients.find((stpIng: [Step, Ingredient]) => stpIng[0].id === stepIngredient[0].id && stpIng[1].id === stepIngredient[1].id);
            if (stpIng && stpIng[1].quantity !== undefined) {
                return stpIng[1].quantity;
            }
            return 0;
        };
    }

    get productPrice() {
        let productPrice = 0;
        if (this.currentProduct?.price || this.productType?.custom_price) {
            if (this.currentProduct?.price) {
                productPrice = this.currentProduct.price;
            } else if (!this.currentProduct && this.productType?.custom_price) {
                productPrice = this.productType.custom_price;
            }
            if (typeof productPrice === 'string') {
                productPrice = parseFloat(productPrice);
            }
            const selectedIngredients = this.stepsIngredients.filter((stepIng: [Step, Ingredient]) => this.isIngredientSelected(stepIng));
            for (let index = 0; index < selectedIngredients.length; index++) {
                if (selectedIngredients[index][1].price) {
                    if (selectedIngredients[index][1].quantifiable) {
                        productPrice += selectedIngredients[index][1]?.price * selectedIngredients[index][1].quantity!;
                    } else {
                        productPrice += selectedIngredients[index][1].price;
                    }
                }
            }
            for (let index = 0; index < this.stepsProductOptions.length; index++) {
                const productOption = this.stepsProductOptions[index][1];
                if (productOption.type === OptionType.CHECKBOX && productOption.price) {
                    if (productOption.value == 'true' || productOption.value == 'yes') {
                        productPrice += productOption.price;
                    }
                }
            }
        }
        return productPrice;
    }

    get cartProduct() {
        if (this.productType) {
            if (this.isDirty || !this.currentProduct) {
                const product = new Product({
                    id: new Date().getTime(),
                    name: this.currentProduct ? this.currentProduct.name : `Custom ${this.productType!.name}`,
                    slug: this.currentProduct ? this.currentProduct.slug : '',
                    position: this.currentProduct?.position || 0,
                    availability: ProductAvailability.PERMANENT,
                    price: this.productPrice,
                    description: this.currentProduct ? this.currentProduct.description : '',
                    is_active: true,
                    schedule: this.currentProduct ? this.currentProduct.schedule : {},
                    in_stock: false,
                    is_customisable: false,
                    type: this.productType,
                    quantity: this.quantity,
                    is_preset: false,
                    origin: this.currentProduct!,
                    instructions: this.productInstructions,
                    ingredients: this.stepsIngredients.map((stepIngredient: [Step, Ingredient]) => stepIngredient[1]),
                    options: this.stepsProductOptions.map((stepProductOption: [Step, ProductOption]) => stepProductOption[1]),
                    image: this.currentProduct ? this.currentProduct.image : this.productType.image,
                });
                return product;
            } else if (this.currentProduct) {
                const product = new Product(this.currentProduct);
                product.quantity = this.quantity;
                product.ingredients = this.stepsIngredients.map((stepIngredient: [Step, Ingredient]) => stepIngredient[1]);
                product.options = this.stepsProductOptions.map((stepProductOption: [Step, ProductOption]) => stepProductOption[1]);
                return product;
            }
        }
        return null;
    }

    get stepProductOptions() {
        return (step: Step) => this.stepsProductOptions.filter((stepProductOption: [Step, ProductOption]) => stepProductOption[0].id === step.id).map((stepProductOption) => stepProductOption[1]);
    }
}
